import type { StageDTO } from '@devrobot/shared';
import { Ban, Circle, CircleCheck, CircleMinus, CircleX, LoaderCircle } from 'lucide-react';
import { useNow } from '../hooks/useNow';
import { durationBetween, formatDuration } from '../lib/format';
import { StatusBadge } from './StatusBadge';

const ICON: Record<string, { Icon: typeof Circle; chip: string; icon?: string }> = {
  succeeded: { Icon: CircleCheck, chip: 'dr-tint-success' },
  failed: { Icon: CircleX, chip: 'dr-tint-error' },
  running: { Icon: LoaderCircle, chip: 'dr-tint-info', icon: 'animate-spin' },
  skipped: { Icon: CircleMinus, chip: 'bg-base-200 text-base-content/45' },
  canceled: { Icon: Ban, chip: 'dr-tint-warning' },
  pending: { Icon: Circle, chip: 'bg-base-200 text-base-content/35' },
};

/** Vertical timeline of job stages with state, duration and message. */
export function JobStages({ stages }: { stages: StageDTO[] }) {
  const now = useNow();
  if (stages.length === 0) return <p className="text-sm text-base-content/60">No stages recorded yet.</p>;
  return (
    <ul className="timeline timeline-vertical timeline-compact timeline-snap-icon -ml-1 [&>li]:[--timeline-col-start:0]">
      {stages.map((st, i) => {
        const { Icon, chip, icon = '' } = ICON[st.state] ?? ICON.pending;
        const done = st.state === 'succeeded';
        const duration = durationBetween(st.startedAt, st.finishedAt, now);
        return (
          <li key={`${st.name}-${i}`} className="animate-rise">
            {i > 0 ? <hr className={stages[i - 1].state === 'succeeded' ? 'bg-success/45' : 'bg-hairline'} /> : null}
            <div className="timeline-middle">
              <span className={`${chip} grid size-7 place-items-center rounded-full`}>
                <Icon className={`size-3.5 ${icon}`} aria-hidden="true" />
              </span>
            </div>
            <div className={`timeline-end w-full min-w-0 pl-2 ${i < stages.length - 1 ? 'mb-5' : ''}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className={`font-semibold ${st.state === 'pending' || st.state === 'skipped' ? 'text-base-content/60' : ''}`}>{st.name}</span>
                <StatusBadge kind="stage" value={st.state} size="xs" />
                {duration !== null ? (
                  <span className="ml-auto text-xs text-base-content/55 tabular-nums">
                    {formatDuration(duration)}
                    {st.finishedAt ? '' : ' so far'}
                  </span>
                ) : null}
              </div>
              {st.message ? <p className={`mt-1 break-anywhere text-sm ${st.state === 'failed' ? 'dr-ink-error' : 'text-base-content/65'}`}>{st.message}</p> : null}
            </div>
            {i < stages.length - 1 ? <hr className={done ? 'bg-success/45' : 'bg-hairline'} /> : null}
          </li>
        );
      })}
    </ul>
  );
}
